import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DesignatableRecordTypeSchema,
  PermissionDeclarationSchema,
  SensitiveFieldDesignationSchema,
  SodRecordTypeSchema,
  SodRuleSchema,
  type SensitiveFieldDesignationDto,
  type SodRuleDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { deleteJson, getData, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

const DESIGNATIONS = '/api/v1/platform/sensitive-field-designations';
const SOD_RULES = '/api/v1/platform/sod-rules';

const CATEGORIES = [
  ['salary_compensation', 'Salary and compensation'],
  ['bank_details', 'Bank details'],
  ['identity', 'Identity'],
  ['trn', 'TRN'],
  ['confidential_hr', 'Confidential HR'],
] as const;

const categoryLabel = (code: string) => CATEGORIES.find(([c]) => c === code)?.[1] ?? code;

/**
 * Sensitive-field designations (SDD §12.1; FRD-USR-015 to USR-017, FRD-HRP-018, HRP-022): which
 * fields are masked, and in which category. A designation takes effect on the next request, in
 * every list, record, search result and notification. Seeing a category unmasked needs its own
 * `view_unmasked` grant on the Roles page.
 */
export function SensitiveFieldsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'platform.sensitive_field_designation:create');
  const canChange = hasCapability(session.data, 'platform.sensitive_field_designation:change');
  const [adding, setAdding] = useState(false);
  const [withdrawing, setWithdrawing] = useState<SensitiveFieldDesignationDto | null>(null);
  const queryClient = useQueryClient();
  const designations = useQuery({
    queryKey: ['platform', 'designations'],
    queryFn: () => getData(DESIGNATIONS, z.array(SensitiveFieldDesignationSchema)),
  });
  const withdraw = useMutation({
    mutationFn: (id: string) =>
      deleteJson(
        `${DESIGNATIONS}/${id}`,
        z.object({ withdrawn: z.literal(true) }),
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setWithdrawing(null);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'designations'] });
    },
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Sensitive fields"
        description="Fields that are masked wherever they appear, unless a role grants the category unmasked."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setAdding(true);
              }}
            >
              Designate field
            </Button>
          ) : undefined
        }
      />
      <Section>
        {designations.isPending && <LoadingState label="Loading designations" />}
        {designations.isError && <ErrorState message={designations.error.message} />}
        {designations.data?.length === 0 && <EmptyState title="No field is designated" />}
        {designations.data !== undefined && designations.data.length > 0 && (
          <Table size="small" aria-label="Sensitive field designations">
            <TableHead>
              <TableRow>
                <TableCell>Record type</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Since</TableCell>
                {canChange && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {designations.data.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>{d.recordTypeName}</TableCell>
                  <TableCell>{d.fieldLabel ?? d.field}</TableCell>
                  <TableCell>{categoryLabel(d.sensitivityCategory)}</TableCell>
                  <TableCell>{new Date(d.createdAt).toLocaleDateString()}</TableCell>
                  {canChange && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => {
                          withdraw.reset();
                          setWithdrawing(d);
                        }}
                      >
                        Withdraw
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {adding && (
        <DesignateDialog
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
      <ConfirmDialog
        open={withdrawing !== null}
        title="Withdraw designation"
        message={
          withdrawing === null
            ? ''
            : `${withdrawing.recordTypeName} — ${withdrawing.fieldLabel ?? withdrawing.field} will be shown unmasked to everyone who can see the record, from the next request.`
        }
        confirmLabel="Withdraw"
        tone="warning"
        busy={withdraw.isPending}
        error={withdraw.isError ? withdraw.error.message : null}
        onCancel={() => {
          setWithdrawing(null);
        }}
        onConfirm={() => {
          if (withdrawing !== null) withdraw.mutate(withdrawing.id);
        }}
      />
    </Stack>
  );
}

function DesignateDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [recordType, setRecordType] = useState('');
  const [field, setField] = useState('');
  const [category, setCategory] = useState('');
  const types = useQuery({
    queryKey: ['platform', 'designatable-fields'],
    queryFn: () =>
      getData('/api/v1/platform/designatable-fields', z.array(DesignatableRecordTypeSchema)),
  });
  const fields = types.data?.find((t) => t.recordType === recordType)?.fields ?? [];
  const save = useMutation({
    mutationFn: () =>
      postJson(
        DESIGNATIONS,
        { recordType, field, sensitivityCategory: category },
        SensitiveFieldDesignationSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'designations'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Designate a sensitive field"
      submitLabel="Designate"
      canSubmit={recordType !== '' && field !== '' && category !== ''}
      submitting={save.isPending}
      error={save.isError ? save.error.message : types.isError ? types.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        select
        label="Record type"
        required
        value={recordType}
        onChange={(e) => {
          setRecordType(e.target.value);
          setField('');
        }}
      >
        {(types.data ?? []).map((t) => (
          <MenuItem key={t.recordType} value={t.recordType}>
            {t.displayName}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Field"
        required
        disabled={recordType === ''}
        value={field}
        onChange={(e) => {
          setField(e.target.value);
        }}
      >
        {fields.map((f) => (
          <MenuItem key={f.field} value={f.field}>
            {f.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Category"
        required
        value={category}
        onChange={(e) => {
          setCategory(e.target.value);
        }}
      >
        {CATEGORIES.map(([code, label]) => (
          <MenuItem key={code} value={code}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <Alert severity="info" variant="outlined">
        The field is masked for everyone who lacks this category unmasked on this record type, from
        the next request.
      </Alert>
    </FormDialog>
  );
}

/**
 * Segregation-of-duties rules (SDD §11.5; FRD-USR-019 to USR-021, PRC-006). A rule refuses a
 * controlled action to a user who already performed the conflicting action on the same record or
 * its chain, and names the independent permission required. Which actions conflict is a
 * management decision; only record types whose module supplies the chain of acting users can
 * carry a rule.
 */
export function SodRulesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'platform.sod_rule:create');
  const canChange = hasCapability(session.data, 'platform.sod_rule:change');
  const [adding, setAdding] = useState(false);
  const [toggling, setToggling] = useState<SodRuleDto | null>(null);
  const queryClient = useQueryClient();
  const rules = useQuery({
    queryKey: ['platform', 'sod-rules'],
    queryFn: () => getData(SOD_RULES, z.array(SodRuleSchema)),
  });
  const toggle = useMutation({
    mutationFn: (rule: SodRuleDto) =>
      patchJson(
        `${SOD_RULES}/${rule.code}`,
        { status: rule.status === 'active' ? 'inactive' : 'active' },
        SodRuleSchema,
        session.data?.csrfToken ?? '',
        rule.version,
      ),
    onSuccess: async () => {
      setToggling(null);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'sod-rules'] });
    },
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Segregation of duties"
        description="Rules that stop one user from performing conflicting actions on the same record."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setAdding(true);
              }}
            >
              New rule
            </Button>
          ) : undefined
        }
      />
      <Section>
        {rules.isPending && <LoadingState label="Loading rules" />}
        {rules.isError && <ErrorState message={rules.error.message} />}
        {rules.data?.length === 0 && (
          <EmptyState
            title="No rule is configured"
            detail="Which actions conflict is a management decision; nothing is refused until a rule exists."
          />
        )}
        {rules.data !== undefined && rules.data.length > 0 && (
          <Table size="small" aria-label="Segregation-of-duties rules">
            <TableHead>
              <TableRow>
                <TableCell>Rule</TableCell>
                <TableCell>Record type</TableCell>
                <TableCell>Controlled action</TableCell>
                <TableCell>Conflicting action</TableCell>
                <TableCell>Independent permission</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.data.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>{rule.code}</TableCell>
                  <TableCell>{rule.recordTypeName}</TableCell>
                  <TableCell>{rule.controlledAction}</TableCell>
                  <TableCell>{rule.conflictingAction}</TableCell>
                  <TableCell>{rule.requiredIndependentPermission ?? '—'}</TableCell>
                  <TableCell>
                    <StatusChip status={rule.status} />
                  </TableCell>
                  {canChange && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        color={rule.status === 'active' ? 'warning' : 'primary'}
                        onClick={() => {
                          toggle.reset();
                          setToggling(rule);
                        }}
                      >
                        {rule.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {adding && (
        <SodRuleDialog
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
      <ConfirmDialog
        open={toggling !== null}
        title={toggling?.status === 'active' ? 'Deactivate rule' : 'Activate rule'}
        message={
          toggling?.status === 'active'
            ? `While ${toggling.code} is inactive, the conflicting actions it covers are no longer refused.`
            : `Once ${toggling?.code ?? ''} is active, the controlled action is refused to anyone who performed the conflicting action.`
        }
        confirmLabel={toggling?.status === 'active' ? 'Deactivate' : 'Activate'}
        tone={toggling?.status === 'active' ? 'warning' : 'primary'}
        busy={toggle.isPending}
        error={toggle.isError ? toggle.error.message : null}
        onCancel={() => {
          setToggling(null);
        }}
        onConfirm={() => {
          if (toggling !== null) toggle.mutate(toggling);
        }}
      />
    </Stack>
  );
}

function SodRuleDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [recordType, setRecordType] = useState('');
  const [controlled, setControlled] = useState('');
  const [conflicting, setConflicting] = useState('');
  const [permission, setPermission] = useState('');
  const types = useQuery({
    queryKey: ['platform', 'sod-record-types'],
    queryFn: () => getData('/api/v1/platform/sod-record-types', z.array(SodRecordTypeSchema)),
  });
  const catalogue = useQuery({
    queryKey: ['platform', 'permission-catalogue'],
    queryFn: () =>
      getData('/api/v1/platform/permission-catalogue', z.array(PermissionDeclarationSchema)),
  });
  const type = types.data?.find((t) => t.recordType === recordType);
  const save = useMutation({
    mutationFn: () =>
      postJson(
        SOD_RULES,
        {
          code: code.trim(),
          recordType,
          controlledAction: controlled,
          conflictingAction: conflicting.trim(),
          ...(permission !== '' ? { requiredIndependentPermission: permission } : {}),
        },
        SodRuleSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'sod-rules'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="New segregation-of-duties rule"
      submitLabel="Create rule"
      canSubmit={
        /^[a-z][a-z0-9_]*$/.test(code.trim()) &&
        recordType !== '' &&
        controlled !== '' &&
        conflicting.trim() !== ''
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : types.isError ? types.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Rule code"
        required
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
        }}
        helperText="Lower snake case, e.g. invoice_post_not_by_creator."
      />
      <TextField
        select
        label="Record type"
        required
        value={recordType}
        onChange={(e) => {
          setRecordType(e.target.value);
          setControlled('');
          setConflicting('');
        }}
        helperText="Only record types whose module supplies the chain of acting users."
      >
        {(types.data ?? []).map((t) => (
          <MenuItem key={t.recordType} value={t.recordType}>
            {t.displayName}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Controlled action"
        required
        disabled={type === undefined}
        value={controlled}
        onChange={(e) => {
          setControlled(e.target.value);
        }}
      >
        {(type?.actions ?? []).map((a) => (
          <MenuItem key={a} value={a}>
            {a}
          </MenuItem>
        ))}
      </TextField>
      {type !== undefined && type.conflictingActions.length > 0 ? (
        <TextField
          select
          label="Conflicting action"
          required
          value={conflicting}
          onChange={(e) => {
            setConflicting(e.target.value);
          }}
        >
          {type.conflictingActions.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField
          label="Conflicting action"
          required
          disabled={type === undefined}
          value={conflicting}
          onChange={(e) => {
            setConflicting(e.target.value);
          }}
          helperText="An action on this record (e.g. create) or on a related record, as <record type>:<action>."
        />
      )}
      <TextField
        select
        label="Independent permission (named in the refusal)"
        value={permission}
        onChange={(e) => {
          setPermission(e.target.value);
        }}
      >
        <MenuItem value="">Default: the controlled permission</MenuItem>
        {(catalogue.data ?? []).map((p) => (
          <MenuItem key={`${p.recordType}:${p.action}`} value={`${p.recordType}:${p.action}`}>
            {p.recordType}:{p.action}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}
